import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Package, FolderTree, Ticket, Users, Settings as SettingsIcon,
  LogOut, Plus, Pencil, Trash2, X, ExternalLink, Copy, QrCode, CreditCard, Search, FileText, UserCheck, Rocket, CalendarCheck,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";
import { api, formatApiErrorDetail } from "../lib/api";

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "products", label: "Prodotti", icon: Package },
  { key: "categories", label: "Categorie", icon: FolderTree },
  { key: "referrals", label: "Referral / QR", icon: Ticket },
  { key: "fidelity", label: "Carta Fedeltà", icon: CreditCard },
  { key: "leads", label: "Lead", icon: Users },
  { key: "customers", label: "Clienti", icon: UserCheck },
  { key: "landings", label: "Landing", icon: Rocket },
  { key: "bookings", label: "Prenotazioni", icon: CalendarCheck },
  { key: "content", label: "Footer & Pagine", icon: FileText },
  { key: "settings", label: "Impostazioni", icon: SettingsIcon },
];

function Modal({ title, onClose, children, size = "max-w-lg" }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 overflow-y-auto bg-ink/60 backdrop-blur-sm animate-fade-in">
      <div className={`w-full ${size} bg-white border border-line shadow-2xl my-8 animate-scale-in`}>
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <h3 className="font-heading font-bold text-lg text-ink">{title}</h3>
          <button onClick={onClose} data-testid="modal-close" className="text-ink-muted hover:text-ink">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-transparent border border-line focus:border-brand px-3 py-2 outline-none transition-colors text-ink text-sm";
const labelCls = "label-eyebrow text-ink-muted block mb-1.5";

function Field({ label, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const { refresh } = useSite();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [user, loading, navigate]);

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center text-ink-muted">Caricamento…</div>;
  }

  return (
    <div className="min-h-screen flex bg-surface">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-white border-r border-line flex flex-col sticky top-0 h-screen">
        <div className="px-6 py-5 border-b border-line font-heading font-black text-lg text-ink">
          Admin<span className="text-brand">.</span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <button
              key={n.key}
              onClick={() => setTab(n.key)}
              data-testid={`admin-nav-${n.key}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                tab === n.key ? "bg-brand text-white" : "text-ink-muted hover:bg-surface-alt hover:text-ink"
              }`}
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-line">
          <button
            onClick={() => { logout(); navigate("/"); }}
            data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-ink-muted hover:text-destructive transition-colors"
          >
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="bg-white border-b border-line px-8 py-4">
          <h1 className="font-heading font-bold text-xl text-ink capitalize">
            {NAV.find((n) => n.key === tab)?.label}
          </h1>
        </div>
        <div className="p-8">
          {tab === "dashboard" && <DashboardTab />}
          {tab === "products" && <ProductsTab />}
          {tab === "categories" && <CategoriesTab onChanged={refresh} />}
          {tab === "referrals" && <ReferralsTab />}
          {tab === "fidelity" && <FidelityTab />}
          {tab === "leads" && <LeadsTab />}
          {tab === "customers" && <CustomersTab />}
          {tab === "landings" && <LandingsTab />}
          {tab === "bookings" && <BookingsTab />}
          {tab === "content" && <ContentTab onSaved={refresh} />}
          {tab === "settings" && <SettingsTab onSaved={refresh} />}
        </div>
      </main>
    </div>
  );
}

/* ----------------------------- Dashboard ----------------------------- */
function DashboardTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  const cards = [
    { label: "Lead totali", value: stats?.total_leads, testid: "stat-leads" },
    { label: "Prodotti", value: stats?.total_products, testid: "stat-products" },
    { label: "Referral attivi", value: stats?.total_referrals, testid: "stat-referrals" },
    { label: "Carte fedeltà", value: stats?.total_cards, testid: "stat-cards" },
    { label: "Clienti registrati", value: stats?.total_customers, testid: "stat-customers" },
    { label: "Prenotazioni", value: stats?.total_bookings, testid: "stat-bookings" },
    { label: "Lead con referral", value: stats?.leads_with_referral, testid: "stat-leads-ref" },
  ];
  return (
    <div className="grid gap-px bg-line border border-line sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((c) => (
        <div key={c.label} className="bg-white p-6" data-testid={c.testid}>
          <div className="label-eyebrow text-ink-muted">{c.label}</div>
          <div className="mt-3 font-heading font-black text-4xl text-brand">{c.value ?? "—"}</div>
        </div>
      ))}
    </div>
  );
}

/* ----------------------------- Products ----------------------------- */
const emptyProduct = {
  name: "", category: "", subcategory: "", segment: "", description: "", price: "", features: "", badge: "",
  points: 0, active: true, whatsapp_message_template: "", order: 0,
};

function ProductsTab() {
  const { categories } = useSite();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // product object or null
  const [form, setForm] = useState(emptyProduct);
  const [query, setQuery] = useState("");
  const [filterCat, setFilterCat] = useState("");

  const load = () => api.get("/admin/products").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ ...emptyProduct, category: categories[0]?.slug || "" });
    setEditing("new");
  };
  const openEdit = (p) => {
    setForm({ ...p, features: (p.features || []).join("\n") });
    setEditing(p);
  };

  const save = async () => {
    const payload = {
      ...form,
      order: Number(form.order) || 0,
      points: Number(form.points) || 0,
      features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing === "new") await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${editing.id}`, payload);
      toast.success("Prodotto salvato");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare "${p.name}"?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Prodotto eliminato");
    load();
  };

  const updatePoints = async (p, value) => {
    const points = Number(value) || 0;
    if (points === (p.points || 0)) return;
    try {
      await api.put(`/admin/products/${p.id}`, { ...p, points });
      toast.success("Punti aggiornati");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const catName = (slug) => categories.find((c) => c.slug === slug)?.name || slug;

  const filtered = items.filter((p) => {
    const matchesCat = !filterCat || p.category === filterCat;
    const q = query.trim().toLowerCase();
    const matchesQuery = !q ||
      p.name.toLowerCase().includes(q) ||
      catName(p.category).toLowerCase().includes(q) ||
      (p.subcategory || "").toLowerCase().includes(q);
    return matchesCat && matchesQuery;
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-ink-muted">{filtered.length} di {items.length} prodotti</p>
        <button onClick={openNew} data-testid="add-product-btn" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-2 rounded-full transition-colors">
          <Plus className="w-4 h-4" /> Nuovo prodotto
        </button>
      </div>

      {/* Search & filter */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="product-search-input"
            placeholder="Cerca per nome, categoria o sottocategoria…"
            className={`${inputCls} pl-9`}
          />
        </div>
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          data-testid="product-filter-category"
          className={`${inputCls} sm:w-56`}
        >
          <option value="">Tutte le categorie</option>
          {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
      </div>

      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-alt text-ink-muted">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold">Nome</th>
              <th className="px-4 py-3 font-semibold">Categoria</th>
              <th className="px-4 py-3 font-semibold">Prezzo</th>
              <th className="px-4 py-3 font-semibold">Punti</th>
              <th className="px-4 py-3 font-semibold">Stato</th>
              <th className="px-4 py-3 font-semibold text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-t border-line" data-testid={`product-row-${p.id}`}>
                <td className="px-4 py-3 font-medium text-ink">{p.name}</td>
                <td className="px-4 py-3 text-ink-muted">{catName(p.category)}</td>
                <td className="px-4 py-3 text-ink-muted">{p.price || "—"}</td>
                <td className="px-4 py-3">
                  <input
                    type="number"
                    defaultValue={p.points || 0}
                    onBlur={(e) => updatePoints(p, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    data-testid={`product-points-inline-${p.id}`}
                    className="w-16 border border-line rounded px-2 py-1 text-sm focus:border-brand outline-none"
                  />
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 ${p.active ? "bg-wa/15 text-wa-dark" : "bg-line text-ink-muted"}`}>
                    {p.active ? "Attivo" : "Off"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="text-ink-muted hover:text-brand"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(p)} data-testid={`delete-product-${p.id}`} className="text-ink-muted hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-ink-muted">Nessun prodotto trovato.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal title={editing === "new" ? "Nuovo prodotto" : "Modifica prodotto"} onClose={() => setEditing(null)}>
          <div className="space-y-4">
            <Field label="Nome">
              <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Categoria">
                <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-category-select">
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Prezzo (testo)">
                <input className={inputCls} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="€24,90/mese" data-testid="product-price-input" />
              </Field>
            </div>
            <Field label="Sottocategoria (opzionale)">
              <select className={inputCls} value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} data-testid="product-subcategory-select">
                <option value="">Nessuna</option>
                {(categories.find((c) => c.slug === form.category)?.subcategories || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            {(categories.find((c) => c.slug === form.category)?.segments || []).length > 0 && (
              <Field label="Segmento (Privato / Business)">
                <select className={inputCls} value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })} data-testid="product-segment-select">
                  <option value="">Nessuno</option>
                  {(categories.find((c) => c.slug === form.category)?.segments || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            )}
            <Field label="Descrizione">
              <textarea className={inputCls} rows="2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
            <Field label="Caratteristiche (una per riga)">
              <textarea className={inputCls} rows="3" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} placeholder={"Fibra 1 Gbps\nChiamate illimitate"} />
            </Field>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Badge (es. PROMO)">
                <input className={inputCls} value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} />
              </Field>
              <Field label="Punti fedeltà">
                <input type="number" className={inputCls} value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} data-testid="product-points-input" placeholder="0" />
              </Field>
              <Field label="Ordine">
                <input type="number" className={inputCls} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
              </Field>
            </div>
            <Field label="Template messaggio WhatsApp (opzionale)">
              <textarea className={inputCls} rows="2" value={form.whatsapp_message_template} onChange={(e) => setForm({ ...form, whatsapp_message_template: e.target.value })} placeholder="Usa [NOME PRODOTTO], [CATEGORIA], [CODICE]" />
            </Field>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="product-active-checkbox" />
              Attivo
            </label>
            <button onClick={save} data-testid="save-product-btn" className="w-full bg-brand hover:bg-brand-dark text-white font-medium py-2.5 transition-colors">Salva</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ----------------------------- Categories ----------------------------- */
const emptyCat = { name: "", slug: "", description: "", icon: "Zap", image_url: "", subcategories: "", segments: "", active: true, order: 0 };
const ICONS = ["Wifi", "Router", "Signal", "Sun", "Zap", "Flame", "Droplet", "Tv", "Globe", "Phone", "Smartphone", "ShieldCheck", "Wallet", "Car", "Gift", "Building2", "Home", "Leaf", "Star"];

function CategoriesTab({ onChanged }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCat);

  const load = () => api.get("/admin/categories").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      ...form,
      order: Number(form.order) || 0,
      subcategories: (form.subcategories || "").split("\n").map((s) => s.trim()).filter(Boolean),
      segments: (form.segments || "").split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing === "new") await api.post("/admin/categories", payload);
      else await api.put(`/admin/categories/${editing.id}`, payload);
      toast.success("Categoria salvata");
      setEditing(null);
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare la categoria "${c.name}"?`)) return;
    await api.delete(`/admin/categories/${c.id}`);
    toast.success("Categoria eliminata");
    load();
    onChanged && onChanged();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-ink-muted">{items.length} categorie</p>
        <button onClick={() => { setForm(emptyCat); setEditing("new"); }} data-testid="add-category-btn" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-2 transition-colors">
          <Plus className="w-4 h-4" /> Nuova categoria
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-line p-5" data-testid={`category-card-${c.slug}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-heading font-bold text-ink">{c.name}</div>
                <div className="text-xs text-ink-muted font-mono mt-0.5">/{c.slug}</div>
              </div>
              <span className={`text-xs px-2 py-0.5 ${c.active ? "bg-wa/15 text-wa-dark" : "bg-line text-ink-muted"}`}>{c.active ? "Attiva" : "Off"}</span>
            </div>
            <p className="mt-3 text-sm text-ink-muted line-clamp-2">{c.description}</p>
            <div className="mt-4 flex gap-2">
              <button onClick={() => { setForm({ ...c, subcategories: (c.subcategories || []).join("\n"), segments: (c.segments || []).join("\n") }); setEditing(c); }} data-testid={`edit-category-${c.slug}`} className="text-ink-muted hover:text-brand"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(c)} data-testid={`delete-category-${c.slug}`} className="text-ink-muted hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing === "new" ? "Nuova categoria" : "Modifica categoria"} onClose={() => setEditing(null)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Nome"><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-name-input" /></Field>
              <Field label="Slug (url)"><input className={inputCls} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} data-testid="category-slug-input" /></Field>
            </div>
            <Field label="Descrizione"><textarea className={inputCls} rows="2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Icona">
                <select className={inputCls} value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })}>
                  {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </Field>
              <Field label="Ordine"><input type="number" className={inputCls} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></Field>
            </div>
            <Field label="URL immagine"><input className={inputCls} value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></Field>
            <Field label="Sottocategorie (una per riga)">
              <textarea className={inputCls} rows="3" value={form.subcategories} onChange={(e) => setForm({ ...form, subcategories: e.target.value })} data-testid="category-subcategories-input" placeholder={"Fibra\nMobile\nFibra + Mobile"} />
              <p className="text-xs text-ink-muted mt-1.5">Compaiono nel menu a tendina della navbar e come filtri nella pagina categoria.</p>
            </Field>
            <Field label="Segmenti (una per riga, es. Privato / Business)">
              <textarea className={inputCls} rows="2" value={form.segments} onChange={(e) => setForm({ ...form, segments: e.target.value })} data-testid="category-segments-input" placeholder={"Privato\nBusiness"} />
              <p className="text-xs text-ink-muted mt-1.5">Se valorizzati, ogni sottocategoria si apre in un secondo livello (es. Privato/Business). Lascia vuoto per non usarli.</p>
            </Field>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Attiva
            </label>
            <button onClick={save} data-testid="save-category-btn" className="w-full bg-brand hover:bg-brand-dark text-white font-medium py-2.5 transition-colors">Salva</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ----------------------------- Referrals ----------------------------- */
const emptyRef = { code: "", campaign_name: "", custom_banner_title: "Benvenuto!", custom_banner_message: "", bonus_type: "", discount_value: "", active: true };
const BONUS_TYPES = ["", "Buono Amazon", "Buono regalo", "Sconto in bolletta", "Cashback", "Buono carburante", "Altro"];

function ReferralsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyRef);
  const [qr, setQr] = useState(null);

  const load = () => api.get("/admin/referrals").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing === "new") await api.post("/admin/referrals", form);
      else await api.put(`/admin/referrals/${editing.id}`, form);
      toast.success("Referral salvato");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare il referral "${r.code}"?`)) return;
    await api.delete(`/admin/referrals/${r.id}`);
    toast.success("Referral eliminato");
    load();
  };

  const linkFor = (code) => `${window.location.origin}/?ref=${encodeURIComponent(code)}`;
  const qrFor = (code) => `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(linkFor(code))}`;

  const copyLink = (code) => {
    navigator.clipboard.writeText(linkFor(code));
    toast.success("Link copiato");
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-ink-muted">{items.length} referral</p>
        <button onClick={() => { setForm(emptyRef); setEditing("new"); }} data-testid="add-referral-btn" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-2 transition-colors">
          <Plus className="w-4 h-4" /> Nuovo referral
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {items.map((r) => (
          <div key={r.id} className="bg-white border border-line p-5" data-testid={`referral-card-${r.code}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono font-bold text-brand">{r.code}</div>
                <div className="text-xs text-ink-muted mt-0.5">{r.campaign_name || "—"}</div>
              </div>
              <span className={`text-xs px-2 py-0.5 ${r.active ? "bg-wa/15 text-wa-dark" : "bg-line text-ink-muted"}`}>{r.active ? "Attivo" : "Off"}</span>
            </div>
            <p className="mt-3 text-sm text-ink-muted line-clamp-2">{r.custom_banner_message}</p>
            {(r.bonus_type || r.discount_value) && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-sm bg-brand/10 text-brand px-2.5 py-1">
                <Ticket className="w-3.5 h-3.5" />
                <span className="font-bold">{[r.bonus_type, r.discount_value].filter(Boolean).join(" ")}</span>
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => copyLink(r.code)} data-testid={`copy-link-${r.code}`} className="inline-flex items-center gap-1.5 text-xs border border-line px-2.5 py-1.5 hover:border-brand transition-colors"><Copy className="w-3.5 h-3.5" /> Copia link</button>
              <button onClick={() => setQr(r.code)} data-testid={`qr-${r.code}`} className="inline-flex items-center gap-1.5 text-xs border border-line px-2.5 py-1.5 hover:border-brand transition-colors"><QrCode className="w-3.5 h-3.5" /> QR Code</button>
              <a href={linkFor(r.code)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs border border-line px-2.5 py-1.5 hover:border-brand transition-colors"><ExternalLink className="w-3.5 h-3.5" /> Apri</a>
              <button onClick={() => { setForm(r); setEditing(r); }} className="text-ink-muted hover:text-brand ml-auto"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(r)} className="text-ink-muted hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-ink-muted text-sm">Nessun referral. Creane uno per le tue campagne QR.</div>}
      </div>

      {qr && (
        <Modal title={`QR Code · ${qr}`} onClose={() => setQr(null)}>
          <div className="flex flex-col items-center gap-4">
            <img src={qrFor(qr)} alt="QR" className="border border-line" width="240" height="240" data-testid="qr-image" />
            <div className="text-xs text-ink-muted break-all text-center font-mono">{linkFor(qr)}</div>
            <a href={qrFor(qr)} download={`qr-${qr}.png`} target="_blank" rel="noreferrer" className="bg-brand hover:bg-brand-dark text-white text-sm px-4 py-2 transition-colors">Scarica PNG</a>
          </div>
        </Modal>
      )}

      {editing && (
        <Modal title={editing === "new" ? "Nuovo referral" : "Modifica referral"} onClose={() => setEditing(null)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Codice">
                <input
                  className={`${inputCls} ${editing !== "new" ? "opacity-60 cursor-not-allowed bg-surface" : ""}`}
                  value={form.code}
                  onChange={(e) => { if (editing === "new") setForm({ ...form, code: e.target.value }); }}
                  readOnly={editing !== "new"}
                  data-testid="referral-code-input"
                  placeholder="AGENTE001"
                />
                {editing !== "new" && <p className="text-xs text-ink-muted mt-1.5">Il codice non è modificabile: garantisce che il QR code resti invariato.</p>}
              </Field>
              <Field label="Campagna"><input className={inputCls} value={form.campaign_name} onChange={(e) => setForm({ ...form, campaign_name: e.target.value })} placeholder="promo-famiglia" /></Field>
            </div>
            <Field label="Titolo banner"><input className={inputCls} value={form.custom_banner_title} onChange={(e) => setForm({ ...form, custom_banner_title: e.target.value })} data-testid="referral-banner-title-input" /></Field>
            <Field label="Messaggio banner"><textarea className={inputCls} rows="3" value={form.custom_banner_message} onChange={(e) => setForm({ ...form, custom_banner_message: e.target.value })} data-testid="referral-banner-message-input" placeholder="Attivando un servizio ricevi un buono Amazon da 50€!" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Tipo bonus">
                <select className={inputCls} value={form.bonus_type} onChange={(e) => setForm({ ...form, bonus_type: e.target.value })} data-testid="referral-bonus-type-select">
                  {BONUS_TYPES.map((b) => <option key={b} value={b}>{b || "Nessuno"}</option>)}
                </select>
              </Field>
              <Field label="Valore bonus"><input className={inputCls} value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} placeholder="50€ / 10%" data-testid="referral-bonus-value-input" /></Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Attivo
            </label>
            <button onClick={save} data-testid="save-referral-btn" className="w-full bg-brand hover:bg-brand-dark text-white font-medium py-2.5 transition-colors">Salva</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ----------------------------- Leads ----------------------------- */
function LeadsTab() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [onlyReferral, setOnlyReferral] = useState(false);
  useEffect(() => { api.get("/admin/leads").then(({ data }) => setItems(data)).catch(() => {}); }, []);

  const filtered = items.filter((l) => {
    if (onlyReferral && !l.referral_code) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [l.customer_name, l.selected_product, l.selected_category, l.referral_code, l.bonus_type]
      .filter(Boolean).some((v) => v.toLowerCase().includes(q));
  });

  return (
    <div>
      <div className="mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="lead-search-input"
            placeholder="Cerca per cliente, prodotto, categoria o codice referral…"
            className={`${inputCls} pl-9`}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-ink whitespace-nowrap" data-testid="lead-only-referral">
          <input type="checkbox" checked={onlyReferral} onChange={(e) => setOnlyReferral(e.target.checked)} />
          Solo con referral
        </label>
        <span className="text-sm text-ink-muted whitespace-nowrap">{filtered.length} di {items.length}</span>
      </div>

      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-alt text-ink-muted">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold">Data</th>
              <th className="px-4 py-3 font-semibold">Cliente</th>
              <th className="px-4 py-3 font-semibold">Prodotto</th>
              <th className="px-4 py-3 font-semibold">Categoria</th>
              <th className="px-4 py-3 font-semibold">Referral</th>
              <th className="px-4 py-3 font-semibold">Bonus</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.id} className="border-t border-line" data-testid={`lead-row-${l.id}`}>
                <td className="px-4 py-3 text-ink-muted whitespace-nowrap">{new Date(l.timestamp).toLocaleString("it-IT")}</td>
                <td className="px-4 py-3 font-medium text-ink">{l.customer_name || "—"}</td>
                <td className="px-4 py-3 text-ink">{l.selected_product || "—"}</td>
                <td className="px-4 py-3 text-ink-muted">{l.selected_category || "—"}</td>
                <td className="px-4 py-3">{l.referral_code ? <span className="font-mono text-brand">{l.referral_code}</span> : <span className="text-ink-muted">—</span>}</td>
                <td className="px-4 py-3">
                  {(l.bonus_type || l.bonus_value)
                    ? <span className="inline-flex items-center gap-1 text-xs bg-brand/10 text-brand px-2 py-0.5 font-semibold">{[l.bonus_type, l.bonus_value].filter(Boolean).join(" ")}</span>
                    : <span className="text-ink-muted">—</span>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-ink-muted">Nessun lead trovato.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ----------------------------- Customers ----------------------------- */
function CustomersTab() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  useEffect(() => { api.get("/admin/customers").then(({ data }) => setItems(data)).catch(() => {}); }, []);

  const filtered = items.filter((c) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [c.name, c.email, c.phone, c.card_number].filter(Boolean).some((v) => v.toLowerCase().includes(q));
  });

  return (
    <div>
      <div className="mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="customer-search-input"
            placeholder="Cerca per nome, email, telefono o numero carta…"
            className={`${inputCls} pl-9`}
          />
        </div>
        <span className="text-sm text-ink-muted whitespace-nowrap">{filtered.length} di {items.length}</span>
      </div>

      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-alt text-ink-muted">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold">Registrato</th>
              <th className="px-4 py-3 font-semibold">Nome</th>
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold">Telefono</th>
              <th className="px-4 py-3 font-semibold">Carta</th>
              <th className="px-4 py-3 font-semibold">Punti</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-line" data-testid={`customer-row-${c.id}`}>
                <td className="px-4 py-3 text-ink-muted whitespace-nowrap">{c.created_at ? new Date(c.created_at).toLocaleDateString("it-IT") : "—"}</td>
                <td className="px-4 py-3 font-medium text-ink">{c.name || "—"}</td>
                <td className="px-4 py-3 text-ink">{c.email}</td>
                <td className="px-4 py-3 text-ink-muted">{c.phone || "—"}</td>
                <td className="px-4 py-3"><span className="font-mono text-brand">{c.card_number || "—"}</span></td>
                <td className="px-4 py-3 font-heading font-bold text-brand">{c.points}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-ink-muted">Nessun cliente registrato.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ----------------------------- Landings ----------------------------- */
const emptyLanding = { slug: "", title: "", subtitle: "", hero_image: "", description: "", badge: "", benefits: "", items: [], active: true };
const emptyItem = { name: "", description: "", full_price: "", price: "", benefits: "", image_url: "" };

function LandingsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyLanding);

  const load = () => api.get("/admin/landings").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyLanding); setEditing("new"); };
  const openEdit = (l) => {
    setForm({
      ...l,
      benefits: (l.benefits || []).join("\n"),
      items: (l.items || []).map((it) => ({ ...it, benefits: (it.benefits || []).join("\n") })),
    });
    setEditing(l);
  };

  const save = async () => {
    const payload = {
      ...form,
      benefits: (form.benefits || "").split("\n").map((s) => s.trim()).filter(Boolean),
      items: (form.items || [])
        .filter((it) => (it.name || "").trim())
        .map((it) => ({ ...it, benefits: (typeof it.benefits === "string" ? it.benefits : (it.benefits || []).join("\n")).split("\n").map((s) => s.trim()).filter(Boolean) })),
    };
    try {
      if (editing === "new") await api.post("/admin/landings", payload);
      else await api.put(`/admin/landings/${editing.id}`, payload);
      toast.success("Landing salvata");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (l) => {
    if (!window.confirm(`Eliminare la landing "${l.title}"?`)) return;
    await api.delete(`/admin/landings/${l.id}`);
    toast.success("Landing eliminata");
    load();
  };

  const setItem = (idx, key, value) => setForm((f) => {
    const next = [...f.items];
    next[idx] = { ...next[idx], [key]: value };
    return { ...f, items: next };
  });
  const addItem = () => setForm((f) => ({ ...f, items: [...(f.items || []), { ...emptyItem }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const publicUrl = (slug) => `${window.location.origin}/promo/${slug}`;

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-ink-muted">{items.length} landing</p>
        <button onClick={openNew} data-testid="add-landing-btn" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-2 rounded-full transition-colors">
          <Plus className="w-4 h-4" /> Nuova landing
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((l) => (
          <div key={l.id} className="bg-white border border-line p-5" data-testid={`landing-card-${l.slug}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-heading font-bold text-ink truncate">{l.title}</div>
                <div className="text-xs text-ink-muted font-mono mt-0.5">/promo/{l.slug}</div>
              </div>
              <span className={`text-xs px-2 py-0.5 shrink-0 ${l.active ? "bg-wa/15 text-wa-dark" : "bg-line text-ink-muted"}`}>{l.active ? "Attiva" : "Off"}</span>
            </div>
            <div className="mt-2 text-xs text-ink-muted">{(l.items || []).length} prodotti · {(l.benefits || []).length} vantaggi</div>
            <div className="mt-4 flex items-center gap-2">
              <a href={publicUrl(l.slug)} target="_blank" rel="noopener noreferrer" data-testid={`landing-open-${l.slug}`} className="text-ink-muted hover:text-brand" title="Apri landing"><ExternalLink className="w-4 h-4" /></a>
              <button onClick={() => { navigator.clipboard.writeText(publicUrl(l.slug)); toast.success("Link copiato!"); }} data-testid={`landing-copy-${l.slug}`} className="text-ink-muted hover:text-brand" title="Copia link"><Copy className="w-4 h-4" /></button>
              <button onClick={() => openEdit(l)} data-testid={`edit-landing-${l.slug}`} className="text-ink-muted hover:text-brand"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(l)} data-testid={`delete-landing-${l.slug}`} className="text-ink-muted hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-ink-muted">Nessuna landing. Creane una per iniziare a raccogliere prenotazioni.</div>}
      </div>

      {editing && (
        <Modal title={editing === "new" ? "Nuova landing" : "Modifica landing"} onClose={() => setEditing(null)} size="max-w-2xl">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Titolo"><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="landing-title-input" /></Field>
              <Field label="Slug (url: /promo/...)"><input className={inputCls} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} data-testid="landing-slug-input" placeholder="black-friday" /></Field>
            </div>
            <Field label="Sottotitolo"><input className={inputCls} value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Badge (es. Offerta limitata)"><input className={inputCls} value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} /></Field>
              <Field label="URL immagine hero"><input className={inputCls} value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} /></Field>
            </div>
            <Field label="Descrizione"><textarea className={inputCls} rows="2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            <Field label="Vantaggi (uno per riga)"><textarea className={inputCls} rows="3" value={form.benefits} onChange={(e) => setForm({ ...form, benefits: e.target.value })} placeholder={"Spedizione gratuita\nAttivazione inclusa"} /></Field>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={labelCls}>Prodotti / modelli</label>
                <button type="button" onClick={addItem} data-testid="landing-add-item" className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:text-brand-dark"><Plus className="w-3.5 h-3.5" /> Aggiungi prodotto</button>
              </div>
              <div className="space-y-3">
                {(form.items || []).map((it, idx) => (
                  <div key={idx} className="border border-line p-3 rounded-lg" data-testid={`landing-item-row-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-ink-muted">Prodotto #{idx + 1}</span>
                      <button type="button" onClick={() => removeItem(idx)} data-testid={`landing-remove-item-${idx}`} className="text-ink-muted hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input className={inputCls} value={it.name} onChange={(e) => setItem(idx, "name", e.target.value)} placeholder="Nome (es. iPhone 15)" data-testid={`landing-item-name-${idx}`} />
                      <input className={inputCls} value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} placeholder="Descrizione breve" />
                      <input className={inputCls} value={it.full_price} onChange={(e) => setItem(idx, "full_price", e.target.value)} placeholder="Prezzo pieno (es. €39/mese)" data-testid={`landing-item-fullprice-${idx}`} />
                      <input className={inputCls} value={it.price} onChange={(e) => setItem(idx, "price", e.target.value)} placeholder="Prezzo con noi (es. €29/mese)" data-testid={`landing-item-price-${idx}`} />
                      <input className={`${inputCls} col-span-2`} value={it.image_url} onChange={(e) => setItem(idx, "image_url", e.target.value)} placeholder="URL immagine" />
                    </div>
                    <textarea className={`${inputCls} mt-2`} rows="2" value={typeof it.benefits === "string" ? it.benefits : (it.benefits || []).join("\n")} onChange={(e) => setItem(idx, "benefits", e.target.value)} placeholder={"Benefici con noi (uno per riga)\nes. Attivazione gratuita\nes. Assistenza dedicata"} data-testid={`landing-item-benefits-${idx}`} />
                  </div>
                ))}
                {(form.items || []).length === 0 && <p className="text-xs text-ink-muted">Nessun prodotto. Aggiungine per mostrarli nella landing e come opzioni nel form di prenotazione.</p>}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="landing-active-checkbox" /> Attiva (visibile pubblicamente)
            </label>
            <button onClick={save} data-testid="save-landing-btn" className="w-full bg-brand hover:bg-brand-dark text-white font-medium py-2.5 transition-colors">Salva landing</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ----------------------------- Bookings ----------------------------- */
function BookingsTab() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");

  const load = () => api.get("/admin/bookings").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (b) => {
    if (!window.confirm(`Eliminare la prenotazione di "${b.name}"?`)) return;
    await api.delete(`/admin/bookings/${b.id}`);
    toast.success("Prenotazione eliminata");
    load();
  };

  const STATUS_OPTIONS = ["Lavorazione", "Venduto", "KO"];
  const statusCls = (s) =>
    s === "Venduto" ? "bg-wa/15 text-wa-dark border-wa/30"
    : s === "KO" ? "bg-destructive/10 text-destructive border-destructive/30"
    : "bg-amber-50 text-amber-700 border-amber-200";

  const setLocal = (id, patch) => setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const saveBooking = async (id, status, notes) => {
    try {
      await api.put(`/admin/bookings/${id}`, { status: status || "Lavorazione", notes: notes || "" });
    } catch (e) {
      toast.error("Errore nel salvataggio");
    }
  };

  const changeStatus = (b, status) => {
    setLocal(b.id, { status });
    saveBooking(b.id, status, b.notes);
    toast.success(`Stato: ${status}`);
  };

  const filtered = items.filter((b) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [b.name, b.phone, b.email, b.service, b.landing_title].filter(Boolean).some((v) => v.toLowerCase().includes(q));
  });

  return (
    <div>
      <div className="mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="booking-search-input" placeholder="Cerca per nome, telefono, email, servizio o landing…" className={`${inputCls} pl-9`} />
        </div>
        <span className="text-sm text-ink-muted whitespace-nowrap">{filtered.length} di {items.length}</span>
      </div>

      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-alt text-ink-muted">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold">Data</th>
              <th className="px-4 py-3 font-semibold">Nome</th>
              <th className="px-4 py-3 font-semibold">Telefono</th>
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold">Servizio</th>
              <th className="px-4 py-3 font-semibold">Landing</th>
              <th className="px-4 py-3 font-semibold">Stato</th>
              <th className="px-4 py-3 font-semibold">Note</th>
              <th className="px-4 py-3 font-semibold text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => (
              <tr key={b.id} className="border-t border-line align-top" data-testid={`booking-row-${b.id}`}>
                <td className="px-4 py-3 text-ink-muted whitespace-nowrap">{b.created_at ? new Date(b.created_at).toLocaleString("it-IT") : "—"}</td>
                <td className="px-4 py-3 font-medium text-ink">{b.name}</td>
                <td className="px-4 py-3 text-ink"><a href={`tel:${(b.phone || "").replace(/[^0-9+]/g, "")}`} className="hover:text-brand">{b.phone}</a></td>
                <td className="px-4 py-3 text-ink-muted">{b.email || "—"}</td>
                <td className="px-4 py-3 text-ink">{b.service || "—"}</td>
                <td className="px-4 py-3 text-ink-muted">{b.landing_title || b.landing_slug}</td>
                <td className="px-4 py-3">
                  <select
                    value={b.status || "Lavorazione"}
                    onChange={(e) => changeStatus(b, e.target.value)}
                    data-testid={`booking-status-${b.id}`}
                    className={`text-xs font-semibold border rounded-full px-2.5 py-1.5 outline-none cursor-pointer ${statusCls(b.status || "Lavorazione")}`}
                  >
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <input
                    value={b.notes || ""}
                    onChange={(e) => setLocal(b.id, { notes: e.target.value })}
                    onBlur={(e) => saveBooking(b.id, b.status, e.target.value)}
                    data-testid={`booking-notes-${b.id}`}
                    placeholder="Aggiungi nota…"
                    className="w-44 bg-surface border border-line focus:border-brand rounded-lg px-2.5 py-1.5 text-sm outline-none transition-colors"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end">
                    <button onClick={() => remove(b)} data-testid={`delete-booking-${b.id}`} className="text-ink-muted hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="9" className="px-4 py-10 text-center text-ink-muted">Nessuna prenotazione.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}



/* ----------------------------- Fidelity ----------------------------- */
const emptyCard = {
  card_number: "", holder_name: "", email: "", phone: "", fiscal_code: "",
  address: "", city: "", birth_date: "", notes: "", active: true,
};

function FidelityTab() {
  const { categories } = useSite();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // "new" | card obj
  const [form, setForm] = useState(emptyCard);
  const [manage, setManage] = useState(null); // card obj being managed (profile)
  const emptyEntry = { product_id: "", service_name: "", category: "", points: 0, date: "", note: "", referred_by: "" };
  const [entryForm, setEntryForm] = useState(emptyEntry);
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);

  useEffect(() => { api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => {}); }, []);

  const categoryName = (slug) => categories.find((c) => c.slug === slug)?.name || slug;

  const selectProduct = (id) => {
    const p = products.find((x) => x.id === id);
    if (!p) { setEntryForm({ ...entryForm, product_id: "", service_name: "", category: "", points: 0 }); return; }
    setEntryForm({ ...entryForm, product_id: id, service_name: p.name, category: categoryName(p.category), points: p.points || 0 });
  };

  const load = (keepManageId) =>
    api.get("/admin/fidelity").then(({ data }) => {
      setItems(data);
      if (keepManageId) {
        const updated = data.find((c) => c.id === keepManageId);
        if (updated) setManage(updated);
      }
    }).catch(() => {});

  useEffect(() => { load(); }, []);

  const saveCard = async () => {
    try {
      if (editing === "new") await api.post("/admin/fidelity", form);
      else await api.put(`/admin/fidelity/${editing.id}`, form);
      toast.success("Anagrafica salvata");
      setEditing(null);
      load(manage?.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeCard = async (c) => {
    if (!window.confirm(`Eliminare la carta "${c.card_number}"?`)) return;
    await api.delete(`/admin/fidelity/${c.id}`);
    toast.success("Carta eliminata");
    load();
  };

  const cardPoints = (c) => (c.entries || []).reduce((s, e) => s + (Number(e.points) || 0), 0);

  const openEdit = (c) => {
    setForm({
      card_number: c.card_number || "", holder_name: c.holder_name || "", email: c.email || "",
      phone: c.phone || "", fiscal_code: c.fiscal_code || "", address: c.address || "",
      city: c.city || "", birth_date: c.birth_date || "", notes: c.notes || "", active: c.active,
    });
    setEditing(c);
  };

  const addEntry = async () => {
    if (!entryForm.service_name.trim()) { toast.error("Seleziona un prodotto o inserisci il servizio"); return; }
    try {
      const { product_id, ...rest } = entryForm;
      const { data } = await api.post(`/admin/fidelity/${manage.id}/entries`, { ...rest, points: Number(entryForm.points) || 0 });
      setManage(data);
      setEntryForm(emptyEntry);
      load(manage.id);
      toast.success("Servizio aggiunto");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeEntry = async (entryId) => {
    const { data } = await api.delete(`/admin/fidelity/${manage.id}/entries/${entryId}`);
    setManage(data);
    load(manage.id);
  };

  const openByCode = async (e) => {
    e?.preventDefault();
    const code = search.trim();
    if (!code) return;
    const local = items.find((c) => c.card_number.toLowerCase() === code.toLowerCase());
    if (local) { setManage(local); return; }
    try {
      const { data } = await api.get("/admin/fidelity");
      const found = data.find((c) => c.card_number.toLowerCase() === code.toLowerCase());
      if (found) { setItems(data); setManage(found); }
      else toast.error("Nessuna carta trovata con questo codice.");
    } catch (err) {
      toast.error("Errore durante la ricerca.");
    }
  };

  const anagrafica = (c) => [
    { label: "Email", value: c.email },
    { label: "Telefono", value: c.phone },
    { label: "Codice Fiscale", value: c.fiscal_code },
    { label: "Data di nascita", value: c.birth_date },
    { label: "Indirizzo", value: c.address },
    { label: "Città", value: c.city },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-ink-muted">{items.length} carte fedeltà</p>
        <button onClick={() => { setForm(emptyCard); setEditing("new"); }} data-testid="add-card-btn" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-2 rounded-full transition-colors">
          <Plus className="w-4 h-4" /> Nuova carta
        </button>
      </div>

      {/* Quick lookup by card number */}
      <form onSubmit={openByCode} className="mb-6 flex flex-col sm:flex-row gap-3 bg-surface border border-line rounded-2xl p-4">
        <div className="relative flex-1">
          <CreditCard className="w-4 h-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-card-search-input"
            placeholder="Inserisci il codice carta per aprire la scheda cliente (es. FID-000123)"
            className={`${inputCls} pl-9`}
          />
        </div>
        <button type="submit" data-testid="admin-card-search-btn" className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white text-sm font-semibold px-5 py-2 rounded-full transition-colors">
          Apri scheda
        </button>
      </form>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-line rounded-2xl p-5" data-testid={`card-row-${c.card_number}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono font-bold text-navy">{c.card_number}</div>
                <div className="text-xs text-ink-muted mt-0.5">{c.holder_name || "—"}</div>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${c.active ? "bg-wa/15 text-wa-dark" : "bg-line text-ink-muted"}`}>{c.active ? "Attiva" : "Off"}</span>
            </div>
            <div className="mt-4 flex items-end justify-between">
              <div>
                <div className="text-[11px] text-ink-muted uppercase tracking-wide">Saldo punti</div>
                <div className="font-heading font-extrabold text-2xl text-brand-dark">{c.points ?? cardPoints(c)}</div>
              </div>
              <div className="text-xs text-ink-muted">{(c.entries || []).length} servizi</div>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setManage(c)} data-testid={`manage-card-${c.card_number}`} className="flex-1 text-xs font-semibold border border-line rounded-lg px-2.5 py-2 hover:border-brand transition-colors">Apri scheda</button>
              <button onClick={() => openEdit(c)} className="text-ink-muted hover:text-brand"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => removeCard(c)} className="text-ink-muted hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-ink-muted text-sm">Nessuna carta. Creane una per il programma fedeltà.</div>}
      </div>

      {/* Create / edit card anagrafica */}
      {editing && (
        <Modal title={editing === "new" ? "Nuova carta fedeltà" : "Modifica anagrafica"} onClose={() => setEditing(null)} size="max-w-2xl">
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Numero carta"><input className={inputCls} value={form.card_number} onChange={(e) => setForm({ ...form, card_number: e.target.value })} data-testid="card-number-input" placeholder="FID-000123" /></Field>
              <Field label="Nome e cognome"><input className={inputCls} value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} data-testid="card-holder-input" placeholder="Mario Rossi" /></Field>
              <Field label="Email"><input className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="card-email-input" placeholder="mario@email.it" /></Field>
              <Field label="Telefono"><input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="card-phone-input" placeholder="+39 333 1234567" /></Field>
              <Field label="Codice Fiscale"><input className={inputCls} value={form.fiscal_code} onChange={(e) => setForm({ ...form, fiscal_code: e.target.value.toUpperCase() })} data-testid="card-fiscal-input" placeholder="RSSMRA80A01H501U" /></Field>
              <Field label="Data di nascita"><input type="date" className={inputCls} value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} data-testid="card-birth-input" /></Field>
              <Field label="Indirizzo"><input className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="card-address-input" placeholder="Via Roma 1" /></Field>
              <Field label="Città"><input className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="card-city-input" placeholder="Milano" /></Field>
            </div>
            <Field label="Note"><textarea className={inputCls} rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note interne sul cliente" /></Field>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Carta attiva
            </label>
            <button onClick={saveCard} data-testid="save-card-btn" className="w-full bg-brand hover:bg-brand-dark text-white font-medium py-2.5 rounded-full transition-colors">Salva anagrafica</button>
          </div>
        </Modal>
      )}

      {/* Customer profile (anagrafica + active services) */}
      {manage && (
        <Modal title={`Scheda cliente · ${manage.card_number}`} onClose={() => setManage(null)} size="max-w-2xl">
          <div className="space-y-6" data-testid="customer-profile">
            {/* Header: name + balance */}
            <div className="rounded-2xl bg-navy text-white p-5 flex items-center justify-between">
              <div>
                <div className="label-eyebrow text-brand">Carta Fedeltà</div>
                <div className="font-heading font-bold text-xl mt-0.5">{manage.holder_name || "Cliente"}</div>
                <div className="font-mono text-sm text-white/70">{manage.card_number}</div>
              </div>
              <div className="text-right">
                <div className="text-[11px] text-white/60 uppercase">Saldo punti</div>
                <div className="font-heading font-extrabold text-4xl text-brand" data-testid="manage-balance">{cardPoints(manage)}</div>
              </div>
            </div>

            {/* Anagrafica */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="label-eyebrow text-ink-muted">Anagrafica cliente</div>
                <button onClick={() => { setManage(null); openEdit(manage); }} data-testid="edit-anagrafica-btn" className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-dark hover:underline">
                  <Pencil className="w-3.5 h-3.5" /> Modifica dati
                </button>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 bg-surface rounded-xl border border-line p-4">
                {anagrafica(manage).map((f) => (
                  <div key={f.label} className="flex flex-col">
                    <span className="text-[11px] text-ink-muted uppercase tracking-wide">{f.label}</span>
                    <span className="text-sm text-navy font-medium">{f.value || "—"}</span>
                  </div>
                ))}
                {manage.notes && (
                  <div className="sm:col-span-2 flex flex-col pt-1">
                    <span className="text-[11px] text-ink-muted uppercase tracking-wide">Note</span>
                    <span className="text-sm text-navy">{manage.notes}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Member-get-member referral link */}
            {manage.referral_code && (
              <div data-testid="member-referral-section">
                <div className="label-eyebrow text-ink-muted mb-3">Presenta un amico</div>
                <div className="bg-brand-light/60 rounded-xl border border-brand/30 p-4">
                  <p className="text-sm text-navy">
                    Condividi questo link con il cliente. Quando una persona presentata attiva un servizio, lui guadagna punti extra.
                  </p>
                  <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <input
                      readOnly
                      value={`${window.location.origin}/?ref=${manage.referral_code}`}
                      data-testid="member-referral-url"
                      className={`${inputCls} font-mono text-xs`}
                      onFocus={(e) => e.target.select()}
                    />
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/?ref=${manage.referral_code}`); toast.success("Link copiato"); }}
                        data-testid="copy-member-url"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold border border-line bg-white rounded-lg px-3 py-2 hover:border-brand transition-colors"
                      >
                        <Copy className="w-3.5 h-3.5" /> Copia
                      </button>
                      <a
                        href={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`${window.location.origin}/?ref=${manage.referral_code}`)}`}
                        download={`qr-${manage.referral_code}.png`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold border border-line bg-white rounded-lg px-3 py-2 hover:border-brand transition-colors"
                      >
                        <QrCode className="w-3.5 h-3.5" /> QR
                      </a>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-ink-muted">Codice presentatore: <span className="font-mono font-semibold text-navy">{manage.referral_code}</span></div>
                </div>
              </div>
            )}

            {/* Active services */}
            <div>
              <div className="label-eyebrow text-ink-muted mb-3">Servizi attivi ({(manage.entries || []).length})</div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {(manage.entries || []).length === 0 && <div className="text-sm text-ink-muted text-center py-4">Nessun servizio attivo registrato.</div>}
                {(manage.entries || []).map((e) => (
                  <div key={e.id} className="flex items-center justify-between border border-line rounded-lg px-3 py-2.5" data-testid={`entry-${e.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-navy truncate">{e.service_name}</div>
                      <div className="text-xs text-ink-muted">{[e.category, e.date].filter(Boolean).join(" · ")}{e.note ? ` · ${e.note}` : ""}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="font-bold text-brand-dark">+{e.points}</span>
                      <button onClick={() => removeEntry(e.id)} className="text-ink-muted hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Add service */}
            <div className="border-t border-line pt-4">
              <div className="label-eyebrow text-ink-muted mb-3">Aggiungi servizio attivato</div>
              <div className="space-y-3">
                <select value={entryForm.product_id} onChange={(e) => selectProduct(e.target.value)} data-testid="entry-product-select" className={inputCls}>
                  <option value="">Seleziona prodotto/offerta (assegna i punti)…</option>
                  {products.filter((p) => p.active).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}{p.points ? ` — +${p.points} punti` : ""}</option>
                  ))}
                </select>
                <input className={inputCls} value={entryForm.service_name} onChange={(e) => setEntryForm({ ...entryForm, service_name: e.target.value })} data-testid="entry-service-input" placeholder="Servizio (compilato dal prodotto, modificabile)" />
                <div className="grid grid-cols-2 gap-3">
                  <select className={inputCls} value={entryForm.category} onChange={(e) => setEntryForm({ ...entryForm, category: e.target.value })} data-testid="entry-category-select">
                    <option value="">Categoria…</option>
                    {categories.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
                  </select>
                  <input type="number" className={inputCls} value={entryForm.points} onChange={(e) => setEntryForm({ ...entryForm, points: e.target.value })} data-testid="entry-points-input" placeholder="Punti" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input type="date" className={inputCls} value={entryForm.date} onChange={(e) => setEntryForm({ ...entryForm, date: e.target.value })} data-testid="entry-date-input" />
                  <input className={inputCls} value={entryForm.note} onChange={(e) => setEntryForm({ ...entryForm, note: e.target.value })} placeholder="Nota (opz.)" />
                </div>
                <input className={inputCls} value={entryForm.referred_by} onChange={(e) => setEntryForm({ ...entryForm, referred_by: e.target.value.toUpperCase() })} data-testid="entry-referred-by-input" placeholder="Codice presentatore (opz.) — es. MGM-AB12CD" />
                <button onClick={addEntry} data-testid="add-entry-btn" className="w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark text-white font-medium py-2.5 rounded-full transition-colors">
                  <Plus className="w-4 h-4" /> Aggiungi e accredita punti
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ----------------------------- Content (Footer & Pages) ----------------------------- */
function ContentTab({ onSaved }) {
  const [settings, setSettings] = useState(null);
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null); // "new" | page obj
  const [pform, setPform] = useState({ slug: "", title: "", content: "", active: true });

  const loadAll = () => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/admin/pages").then(({ data }) => setPages(data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const saveFooter = async () => {
    try {
      await api.put("/admin/settings", settings);
      toast.success("Footer salvato");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const savePage = async () => {
    if (!pform.slug.trim() || !pform.title.trim()) { toast.error("Slug e titolo obbligatori"); return; }
    try {
      if (editing === "new") await api.post("/admin/pages", pform);
      else await api.put(`/admin/pages/${editing.id}`, pform);
      toast.success("Pagina salvata");
      setEditing(null);
      loadAll();
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removePage = async (p) => {
    if (!window.confirm(`Eliminare la pagina "${p.title}"?`)) return;
    await api.delete(`/admin/pages/${p.id}`);
    toast.success("Pagina eliminata");
    loadAll();
    onSaved && onSaved();
  };

  if (!settings) return <div className="text-ink-muted">Caricamento…</div>;

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Footer editor */}
      <div className="bg-white border border-line rounded-2xl p-6">
        <h3 className="font-heading font-bold text-lg text-navy mb-4">Footer del sito</h3>
        <div className="space-y-4">
          <Field label="Descrizione (sotto il logo)">
            <textarea className={inputCls} rows="2" value={settings.footer_description || ""} onChange={(e) => setSettings({ ...settings, footer_description: e.target.value })} data-testid="footer-description-input" placeholder="Es. Fibra, Luce, Gas e Fotovoltaico in un'unica soluzione affidabile." />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Email"><input className={inputCls} value={settings.footer_email || ""} onChange={(e) => setSettings({ ...settings, footer_email: e.target.value })} data-testid="footer-email-input" placeholder="info@enertel.it" /></Field>
            <Field label="Telefono"><input className={inputCls} value={settings.footer_phone || ""} onChange={(e) => setSettings({ ...settings, footer_phone: e.target.value })} data-testid="footer-phone-input" placeholder="+39 02 1234567" /></Field>
            <Field label="Indirizzo"><input className={inputCls} value={settings.footer_address || ""} onChange={(e) => setSettings({ ...settings, footer_address: e.target.value })} data-testid="footer-address-input" placeholder="Via Roma 1, Milano" /></Field>
            <Field label="P.IVA"><input className={inputCls} value={settings.footer_vat || ""} onChange={(e) => setSettings({ ...settings, footer_vat: e.target.value })} data-testid="footer-vat-input" placeholder="IT01234567890" /></Field>
          </div>
          <Field label="Testo copyright">
            <input className={inputCls} value={settings.footer_copyright || ""} onChange={(e) => setSettings({ ...settings, footer_copyright: e.target.value })} data-testid="footer-copyright-input" placeholder="© 2026 Enertel Solutions. Tutti i diritti riservati." />
            <p className="text-xs text-ink-muted mt-1.5">Se vuoto, viene generato automaticamente con anno e nome azienda. La P.IVA viene aggiunta in coda se compilata.</p>
          </Field>
          <button onClick={saveFooter} data-testid="save-footer-btn" className="bg-brand hover:bg-brand-dark text-white font-medium px-6 py-2.5 rounded-full transition-colors">Salva footer</button>
        </div>
      </div>

      {/* Pages manager */}
      <div className="bg-white border border-line rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg text-navy">Pagine legali / informative</h3>
          <button onClick={() => { setPform({ slug: "", title: "", content: "", active: true }); setEditing("new"); }} data-testid="add-page-btn" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-2 rounded-full transition-colors">
            <Plus className="w-4 h-4" /> Nuova pagina
          </button>
        </div>
        <div className="space-y-2">
          {pages.map((p) => (
            <div key={p.id} className="flex items-center justify-between border border-line rounded-lg px-4 py-3" data-testid={`page-row-${p.slug}`}>
              <div className="min-w-0">
                <div className="font-semibold text-navy">{p.title}</div>
                <div className="text-xs text-ink-muted font-mono">/p/{p.slug} {p.active ? "" : "· (non pubblicata)"}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="text-ink-muted hover:text-brand"><ExternalLink className="w-4 h-4" /></a>
                <button onClick={() => { setPform({ slug: p.slug, title: p.title, content: p.content || "", active: p.active }); setEditing(p); }} data-testid={`edit-page-${p.slug}`} className="text-ink-muted hover:text-brand"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => removePage(p)} className="text-ink-muted hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {pages.length === 0 && <div className="text-ink-muted text-sm">Nessuna pagina. Creane una (es. Privacy o Termini).</div>}
        </div>
      </div>

      {editing && (
        <Modal title={editing === "new" ? "Nuova pagina" : "Modifica pagina"} onClose={() => setEditing(null)} size="max-w-2xl">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Titolo"><input className={inputCls} value={pform.title} onChange={(e) => setPform({ ...pform, title: e.target.value })} data-testid="page-title-input" placeholder="Privacy Policy" /></Field>
              <Field label="Slug (URL)"><input className={inputCls} value={pform.slug} onChange={(e) => setPform({ ...pform, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} data-testid="page-slug-input" placeholder="privacy" /></Field>
            </div>
            <Field label="Contenuto">
              <textarea className={`${inputCls} font-mono`} rows="14" value={pform.content} onChange={(e) => setPform({ ...pform, content: e.target.value })} data-testid="page-content-input" placeholder={"Scrivi qui il testo della pagina.\n\nLascia una riga vuota per separare i paragrafi."} />
              <p className="text-xs text-ink-muted mt-1.5">Una riga vuota separa i paragrafi.</p>
            </Field>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={pform.active} onChange={(e) => setPform({ ...pform, active: e.target.checked })} /> Pubblicata
            </label>
            <button onClick={savePage} data-testid="save-page-btn" className="w-full bg-brand hover:bg-brand-dark text-white font-medium py-2.5 rounded-full transition-colors">Salva pagina</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ----------------------------- Settings ----------------------------- */
function SettingsTab({ onSaved }) {
  const [form, setForm] = useState({ whatsapp_number: "", phone_number: "", company_name: "", tagline: "", referral_bonus_points: 50 });
  useEffect(() => { api.get("/settings").then(({ data }) => setForm(data)).catch(() => {}); }, []);

  const save = async () => {
    try {
      await api.put("/admin/settings", { ...form, referral_bonus_points: Number(form.referral_bonus_points) || 0 });
      toast.success("Impostazioni salvate");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-lg bg-white border border-line p-6 space-y-5">
      <Field label="Numero WhatsApp aziendale (con prefisso)">
        <input className={inputCls} value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} placeholder="+39 333 1234567" data-testid="settings-whatsapp-input" />
        <p className="text-xs text-ink-muted mt-1.5">Verrà usato per tutti i pulsanti WhatsApp del sito.</p>
      </Field>
      <Field label="Numero di telefono per chiamate (con prefisso)">
        <input className={inputCls} value={form.phone_number || ""} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} placeholder="+39 06 1234567" data-testid="settings-phone-input" />
        <p className="text-xs text-ink-muted mt-1.5">Usato dal tasto "Parla con noi": al clic parte la chiamata su questo numero.</p>
      </Field>
      <Field label="Nome azienda">
        <input className={inputCls} value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="settings-company-input" />
      </Field>
      <Field label="Slogan / tagline">
        <input className={inputCls} value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
      </Field>
      <Field label="Punti bonus 'Presenta un amico'">
        <input type="number" className={inputCls} value={form.referral_bonus_points} onChange={(e) => setForm({ ...form, referral_bonus_points: e.target.value })} data-testid="settings-referral-bonus-input" placeholder="50" />
        <p className="text-xs text-ink-muted mt-1.5">Punti accreditati a chi presenta, quando il cliente presentato attiva un servizio.</p>
      </Field>
      <button onClick={save} data-testid="save-settings-btn" className="bg-brand hover:bg-brand-dark text-white font-medium px-6 py-2.5 rounded-full transition-colors">Salva impostazioni</button>
    </div>
  );
}
